import type { CustomerRecord } from '@/domain/types';

/**
 * Deterministic "best channel to reach this customer" scorer — no AI. Ranks
 * contact channels from the customer's last inbound channel, age band, tone tag
 * and engagement status. (Relocated out of the former aiExtract.ts, whose
 * AI-assist call/message extraction was removed as out of scope; this scorer is
 * plain business logic used by the deposit-link and queue-work flows.)
 */

export type RecommendedChannel = 'voice' | 'sms' | 'meta-dm' | 'ig-dm' | 'email';

export interface ChannelRecommendation {
  channel: RecommendedChannel;
  reason: string;
  confidence: 'high' | 'medium' | 'low';
}

const CHANNEL_LABEL: Record<RecommendedChannel, string> = {
  voice: 'voice',
  sms: 'SMS',
  'meta-dm': 'Messenger',
  'ig-dm': 'IG DM',
  email: 'email',
};

export function recommendChannel(customer: CustomerRecord): ChannelRecommendation {
  const scores: Record<RecommendedChannel, number> = {
    voice: 0,
    sms: 0,
    'meta-dm': 0,
    'ig-dm': 0,
    email: 0,
  };
  const reasons: string[] = [];

  // Most recent inbound channel is the strongest signal. They reached out that way.
  const lastInbound = [...customer.conversation]
    .reverse()
    .find((e) => e.direction === 'in');
  if (lastInbound) {
    const ch = lastInbound.channel;
    if (ch === 'voice') {
      scores.voice += 25;
      reasons.push('previous voice contact');
    } else if (ch === 'sms' || ch === 'meta-dm' || ch === 'ig-dm' || ch === 'email') {
      scores[ch] += 40;
      reasons.push(`replied last on ${CHANNEL_LABEL[ch]}`);
    }
  }

  // Demographics: younger skews DM, older skews voice.
  if (customer.ageBand === '18-24') {
    scores['ig-dm'] += 20;
    scores.sms += 10;
    reasons.push(`${customer.ageBand} responds to DM`);
  } else if (customer.ageBand === '25-34') {
    scores.sms += 15;
    scores['ig-dm'] += 10;
    reasons.push(`${customer.ageBand} responds to text`);
  } else if (customer.ageBand === '35-44') {
    scores.sms += 10;
    scores.voice += 10;
  } else if (customer.ageBand === '45-54' || customer.ageBand === '55+') {
    scores.voice += 20;
    reasons.push(`${customer.ageBand} prefers voice`);
  }

  // Tone tag.
  if (customer.toneTag === 'casual-young') {
    scores['ig-dm'] += 10;
    scores.sms += 5;
  } else if (customer.toneTag === 'professional') {
    scores.email += 8;
    scores.voice += 5;
  } else if (customer.toneTag === 'reserved') {
    scores.sms += 12;
    reasons.push('reserved tone, text first');
  } else if (customer.toneTag === 'warm-familiar') {
    scores.voice += 10;
    reasons.push('warm rapport, call works');
  }

  // Engagement: dormant clients respond better to a soft text than a cold call.
  if (customer.engagementStatus === 'dormant') {
    scores.sms += 10;
    scores['ig-dm'] += 5;
    reasons.push('dormant, soft approach');
  } else if (customer.engagementStatus === 'active') {
    scores.voice += 5;
  }

  const sorted = (Object.entries(scores) as Array<[RecommendedChannel, number]>).sort(
    (a, b) => b[1] - a[1],
  );
  const top = sorted[0]!;
  const second = sorted[1]!;
  const gap = top[1] - second[1];
  const confidence: ChannelRecommendation['confidence'] =
    gap >= 15 ? 'high' : gap >= 5 ? 'medium' : 'low';

  return {
    channel: top[0],
    reason: reasons.slice(0, 2).join(', ') || 'default channel',
    confidence,
  };
}

/** The channels a suggested REPLY can actually be sent on (no voice/web-form/walk-in). */
export type RepliableChannel = 'sms' | 'meta-dm' | 'ig-dm' | 'email';

/**
 * Agent-queue "suggested reply channel" (tv-6qv.25). This is DISTINCT from
 * recommendChannel: recommendChannel is the neutral "best channel to reach this
 * customer" scorer and still backs the deposit-link glance unchanged. This helper
 * deliberately OVERRIDES that neutral pick for phone-numbered leads — agent leads
 * almost always carry a phone number, so the reply should point to SMS/text
 * rather than an IG/Messenger DM the scorer might otherwise favour.
 *
 * Resolution order:
 *  1. Phone on file (customer.phone non-empty) → 'sms' (the whole point).
 *  2. Otherwise defer to the neutral scorer if it named a messageable channel.
 *  3. If the scorer named voice (or another non-messageable channel), fall back
 *     to the last channel they were actually MESSAGED on, else 'sms'.
 */
export function recommendReplyChannel(customer: CustomerRecord): RepliableChannel {
  // Prefer-phone rule: an agent lead with a number gets an SMS reply.
  if (customer.phone?.trim()) return 'sms';

  const rec = recommendChannel(customer).channel;
  if (rec === 'sms' || rec === 'meta-dm' || rec === 'ig-dm' || rec === 'email') return rec;

  // Scorer landed on voice or a non-messageable channel: fall back to the last
  // channel they were actually messaged on.
  const last = [...customer.conversation]
    .reverse()
    .find(
      (e) =>
        e.channel === 'sms' ||
        e.channel === 'meta-dm' ||
        e.channel === 'ig-dm' ||
        e.channel === 'email',
    );
  return (last?.channel as RepliableChannel | undefined) ?? 'sms';
}
